import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'
import type { Scope } from '../../core/types'
import { useAuth } from '../../core/auth'
import { SCOPE_LABELS } from '../../core/caps'
import { Loading, Empty } from '../../ui/States'
import { useFpdYear, useBuildingRequests, useBuildingTeachers, buildingsFor } from './model'
import { schoolYearOf, trackerRows, focusHours, hoursOf, statusText, type TrackerRow } from './logic'

/**
 * The hours tracker: every Flex PD teacher in the buildings this person sees,
 * against this year's requirement, furthest behind first. Verified hours are
 * banked; asked for and done-not-yet-verified are planned, shown apart,
 * because 8 banked and 6 planned in April is not 14 banked. Principals,
 * learning coaches and the district office; read only.
 */

const n = (h: number) => String(Math.round(h * 100) / 100)

export function Tracker() {
  const { profile } = useAuth()
  const sy = schoolYearOf()
  const year = useFpdYear(sy.id)
  const seeing = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve', 'flexpd.view')
  const [building, setBuilding] = useState<Scope | 'all'>('all')
  const shownB = building === 'all' ? seeing : [building]
  const teachers = useBuildingTeachers(seeing)
  const requests = useBuildingRequests(sy.id, seeing)
  const [show, setShow] = useState<'behind' | 'all'>('all')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const total = year?.totalHours ?? null
  const collab = year?.collabHours ?? null
  const rows = useMemo(() => (teachers && requests
    ? trackerRows(teachers.filter((t) => shownB.includes(t.building)), requests.filter((r) => shownB.includes(r.building)), total, collab)
    : null), [teachers, requests, total, collab, shownB.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!seeing.length) return <Empty title="The tracker is for principals, coaches and the district" body="It shows every teacher’s hours in the buildings you look after." />
  if (year === undefined || !rows || !requests) return <Loading label="Loading the tracker" />

  const done = rows.filter((r) => total != null && r.needed === 0 && r.collabNeeded === 0).length
  const shown = rows
    .filter((r) => show === 'all' || r.needed > 0 || r.collabNeeded > 0)
    .filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()))
  const focus = focusHours(requests.filter((r) => shownB.includes(r.building)))

  return (
    <>
      <Link to="/flex-pd" className="backlink">← Flex PD</Link>
      <h1 className="page-title">Hours tracker</h1>
      <p className="page-sub">{sy.label}{total != null ? ` · ${total} hours required, ${collab ?? 0} collaborative` : ' · this year’s required hours are not set yet'}</p>

      <section className="fpd__progress" style={{ marginTop: 'var(--s-4)' }} aria-label="Summary">
        <div className="fpd__stat"><div className="fpd__k">Teachers</div><div className="fpd__v">{rows.length}</div></div>
        <div className="fpd__stat"><div className="fpd__k">Met the requirement</div><div className="fpd__v">{total == null ? '–' : done}{total != null && <span> of {rows.length}</span>}</div></div>
        <div className="fpd__stat"><div className="fpd__k">Hours verified</div><div className="fpd__v">{n(rows.reduce((s, r) => s + r.banked, 0))}</div>
          <div className="work__sub">{n(rows.reduce((s, r) => s + r.planned, 0))} more planned</div></div>
      </section>

      <div className="fpd__filters">
        {seeing.length > 1 && (
          <div className="chips" role="tablist" aria-label="Building">
            {(['all', ...seeing] as const).map((b) => (
              <button key={b} role="tab" aria-selected={building === b} className={`chip${building === b ? ' is-on' : ''}`} onClick={() => setBuilding(b)}>
                {b === 'all' ? 'Every building' : SCOPE_LABELS[b]}
              </button>
            ))}
          </div>
        )}
        <label className="sel__search"><Search size={15} aria-hidden="true" />
          <input className="opt__input" placeholder="Search a teacher" aria-label="Search a teacher" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="chips" role="tablist" aria-label="Show">
          <button role="tab" aria-selected={show === 'all'} className={`chip${show === 'all' ? ' is-on' : ''}`} onClick={() => setShow('all')}>Everyone</button>
          <button role="tab" aria-selected={show === 'behind'} className={`chip${show === 'behind' ? ' is-on' : ''}`} onClick={() => setShow('behind')}>Still needs hours</button>
        </div>
      </div>

      {shown.length === 0 ? (
        <Empty title={rows.length ? 'Everyone here has met the requirement' : 'No Flex PD teachers yet'}
          body={rows.length ? 'Show everyone to see their hours.' : 'Give certified staff the Flex PD Teacher role in Accounts.'} />
      ) : (
        <>
          <table className="table">
            <thead><tr>
              <th>Teacher</th><th className="num">Verified</th><th className="num">Collaborative</th>
              <th className="num">Planned</th><th className="num">Still needed</th><th>Learning</th>
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <Fragment key={r.email}>
                  <tr className={open === r.email ? 'is-open' : ''}>
                    <td>
                      <button className="row-link" aria-expanded={open === r.email} onClick={() => setOpen(open === r.email ? null : r.email)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {open === r.email ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}{r.name}
                      </button>
                    </td>
                    <td className="num"><Meter have={r.banked} need={total} /></td>
                    <td className="num"><Meter have={r.collabBanked} need={collab} /></td>
                    <td className="num">{n(r.planned)}</td>
                    <td className="num"><Needed r={r} hasTarget={total != null} /></td>
                    <td className="work__sub">{r.focuses.slice(0, 3).join(', ') || '–'}</td>
                  </tr>
                  {open === r.email && (
                    <tr className="is-open"><td colSpan={6}><Requests r={r} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {shown.map((r) => (
              <li key={r.email} className="work__row" style={{ display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong>{r.name}</strong><Needed r={r} hasTarget={total != null} />
                </div>
                <div className="work__sub">{n(r.banked)}{total != null ? ` of ${total}` : ''} verified · {n(r.collabBanked)}{collab != null ? ` of ${collab}` : ''} collaborative · {n(r.planned)} planned</div>
                {r.focuses.length > 0 && <div className="work__sub">{r.focuses.slice(0, 3).join(', ')}</div>}
              </li>
            ))}
          </ul>
        </>
      )}

      {focus.length > 0 && (
        <>
          <h2 className="work__heading">Hours by learning focus</h2>
          <table className="table">
            <thead><tr><th>Learning focus</th><th className="num">Verified</th><th className="num">Planned</th></tr></thead>
            <tbody>{focus.map((f) => <tr key={f.focus}><td>{f.focus}</td><td className="num">{n(f.banked)}</td><td className="num">{n(f.planned)}</td></tr>)}</tbody>
          </table>
          <ul className="work cards">
            {focus.map((f) => <li key={f.focus} className="work__row"><div className="work__main"><strong>{f.focus}</strong><div className="work__sub">{n(f.banked)} verified · {n(f.planned)} planned</div></div></li>)}
          </ul>
        </>
      )}
    </>
  )
}

function Meter({ have, need }: { have: number; need: number | null }) {
  return (
    <span className="fpd__meter">
      <span>{n(have)}{need != null && <span className="work__sub"> / {need}</span>}</span>
      {need ? <span className="meter" aria-hidden="true"><span style={{ width: `${Math.min(100, (have / need) * 100)}%` }} /></span> : null}
    </span>
  )
}

/** Met, or how many to go: in words and colour together. */
function Needed({ r, hasTarget }: { r: TrackerRow; hasTarget: boolean }) {
  if (!hasTarget) return <span className="work__sub">–</span>
  if (r.needed === 0 && r.collabNeeded === 0) return <span className="pill pill--good">Met</span>
  return (
    <span>
      <strong>{n(r.needed)}</strong>
      {r.collabNeeded > 0 && <span className="work__sub"> ({n(r.collabNeeded)} collab.)</span>}
    </span>
  )
}

function Requests({ r }: { r: TrackerRow }) {
  if (!r.requests.length) return <p className="muted-note" style={{ margin: 0 }}>No requests yet this year.</p>
  return (
    <ul className="fpd__reqs">
      {[...r.requests].sort((a, b) => a.date.localeCompare(b.date)).map((x) => (
        <li key={x.id}>
          <Link to={`/flex-pd/${x.id}`}>{x.title || 'Untitled request'}</Link>
          <span className="work__sub"> · {new Date(x.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · {n(hoursOf(x))} h · {x.focus}{x.collaborative ? ' · collaborative' : ''} · {statusText(x, 'principal')}</span>
        </li>
      ))}
    </ul>
  )
}
